import React, { useEffect, useState } from "react";
import { Plus, UserCog, Mail, RefreshCw } from "lucide-react";
import { api, apiError } from "@/lib/apiClient";
import { PageHeader, Loader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_CLS = {
  active: "bg-green-100 text-green-700",
  invited: "bg-amber-100 text-amber-700",
  deactivated: "bg-red-100 text-red-700",
};

export default function AdminStaff() {
  const [staff, setStaff] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "reviewer" });

  const load = async () => setStaff((await api.get("/admin/staff")).data);
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.email) return toast.error("Email is required");
    try {
      await api.post("/admin/staff/invite", form);
      toast.success(`Invitation emailed to ${form.email}`);
      setOpen(false); setForm({ name: "", email: "", role: "reviewer" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const update = async (id, body) => { await api.patch(`/admin/staff/${id}`, body); load(); toast.success("Updated"); };
  const resend = async (id) => { await api.post(`/admin/staff/${id}/resend-invite`); toast.success("Invitation resent"); };

  return (
    <div>
      <PageHeader title="Staff / Team Accounts" subtitle="Invite team members — each gets their own login (up to 20)"
        action={<Button className="rounded-full bg-teal hover:bg-teal/90" onClick={() => setOpen(true)} data-testid="invite-staff"><Plus className="w-4 h-4 mr-1.5" /> Invite Staff</Button>} />

      {!staff ? <Loader /> : (
        <div className="space-y-3">
          {staff.map((s) => (
            <div key={s.id} className="bg-white border border-border rounded-2xl p-4 shadow-sm flex items-center justify-between gap-3 flex-wrap" data-testid={`staff-${s.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-teal/10 text-teal flex items-center justify-center"><UserCog className="w-5 h-5" /></div>
                <div>
                  <p className="font-medium">{s.name || s.email}</p>
                  <p className="text-xs text-muted-foreground">{s.email}</p>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_CLS[s.status] || "bg-slate-100 text-slate-600"}`} data-testid={`staff-status-${s.id}`}>{s.status}</span>
              </div>
              <div className="flex items-center gap-3">
                {s.status === "invited" && (
                  <Button size="sm" variant="ghost" className="text-teal" onClick={() => resend(s.id)} data-testid={`resend-${s.id}`}><RefreshCw className="w-4 h-4 mr-1.5" /> Resend</Button>
                )}
                <Select value={s.role} onValueChange={(v) => update(s.id, { role: v })}>
                  <SelectTrigger className="w-32 h-9" data-testid={`role-${s.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="reviewer">Reviewer</SelectItem>
                    <SelectItem value="support">Support</SelectItem>
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-2 text-xs" title="Active">
                  <Switch checked={s.status === "active"} onCheckedChange={(v) => update(s.id, { is_active: v })} data-testid={`active-${s.id}`} />
                </label>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Invite a Team Member</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Email address</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="teammate@example.com" data-testid="invite-email-input" /></div>
            <div><Label>Name (optional)</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name-input" /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="reviewer">Reviewer</SelectItem>
                  <SelectItem value="support">Support</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> They'll get an email to set their own password and activate their account.</p>
          </div>
          <DialogFooter><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={invite} data-testid="send-invite">Send Invitation</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
